import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { FiscalReceipt, FiscalStatus } from '@/lib/fiscal';

export const caseFiscalReceiptKey = (caseId: number) => ['case-fiscal-receipt', caseId] as const;
export const adminFiscalReceiptKey = (id: number) => ['admin-fiscal-receipt', id] as const;

const pending = (status?: FiscalStatus) => status === 'queued' || status === 'sent';

/** Merchant view: the sale receipt of a case, `null` while none exists yet (404). Polls while the OFD works. */
export function useCaseFiscalReceipt(caseId: number | null | undefined) {
  return useQuery({
    queryKey: caseFiscalReceiptKey(caseId ?? 0),
    enabled: !!caseId,
    refetchInterval: (query) => (query.state.data === null || pending(query.state.data?.status) ? 4000 : false),
    queryFn: async (): Promise<FiscalReceipt | null> => {
      const { data, error, response } = await apiClient.GET('/murabaha/cases/{case}/fiscal-receipt', {
        params: { path: { case: caseId as number } },
      });
      if (response.status === 404) return null;
      if (error) throw error;
      return (data as unknown as { data: FiscalReceipt }).data;
    },
  });
}

export interface AdminFiscalFilters {
  status?: FiscalStatus | 'all';
  merchant_id?: number;
  date_from?: string;
  date_to?: string;
}

export function useAdminFiscalReceipts(filters: AdminFiscalFilters, enabled = true) {
  return useQuery({
    queryKey: ['admin-fiscal-receipts', filters],
    enabled,
    refetchInterval: 30000,
    queryFn: async (): Promise<FiscalReceipt[]> => {
      const query = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v !== undefined && v !== '' && !(k === 'status' && v === 'all')));
      const { data, error } = await apiClient.GET('/admin/fiscal-receipts', { params: { query: query as never } });
      if (error) throw error;
      return (data as unknown as { data: FiscalReceipt[] }).data;
    },
  });
}

export function useAdminFiscalReceipt(id: number | null | undefined) {
  return useQuery({
    queryKey: adminFiscalReceiptKey(id ?? 0),
    enabled: !!id,
    queryFn: async (): Promise<FiscalReceipt> => {
      const { data, error } = await apiClient.GET('/admin/fiscal-receipts/{fiscalReceipt}', { params: { path: { fiscalReceipt: id as number } } });
      if (error) throw error;
      return (data as unknown as { data: FiscalReceipt }).data;
    },
  });
}
